import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import {
  activateOrgEmbeddingRollout,
  disableOrgEmbeddingRollout,
  embeddingRolloutError,
  getOrgEmbeddingRollout,
  OrgEmbeddingRollout,
  startOrgEmbeddingRollout,
  verifyOrgEmbeddingRollout,
} from '../api/orgEmbeddingRollout'

interface OrgEmbeddingRolloutPanelProps {
  orgUuid: string
  localEmbeddingsEnabled?: boolean
  onChanged?: () => void
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const count = (value: number | null) => (value == null ? '—' : value)

const timestamp = (value: string | null) =>
  value ? new Date(value).toLocaleString() : 'Not verified'

const stateColour = (
  state: OrgEmbeddingRollout['state'] | undefined,
): 'default' | 'info' | 'success' | 'warning' | 'error' => {
  if (state === 'enabled' || state === 'ready') return 'success'
  if (state === 'failed') return 'error'
  if (state === 'disabled') return 'default'
  if (state === 'backfilling' || state === 'verifying') return 'info'
  return 'default'
}

export default function OrgEmbeddingRolloutPanel({
  orgUuid,
  localEmbeddingsEnabled = false,
  onChanged,
}: OrgEmbeddingRolloutPanelProps) {
  const [rollout, setRollout] = useState<OrgEmbeddingRollout | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [docsRunId, setDocsRunId] = useState('')
  const [memoriesRunId, setMemoriesRunId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const value = await getOrgEmbeddingRollout(orgUuid)
      setRollout(value)
      setDocsRunId(value?.docs_run_id ?? '')
      setMemoriesRunId(value?.memories_run_id ?? '')
    } catch (loadError) {
      setError(
        embeddingRolloutError(loadError, 'Unable to load embedding rollout.')
          .message,
      )
    } finally {
      setLoading(false)
    }
  }, [orgUuid])

  useEffect(() => {
    void load()
  }, [load])

  const run = async (
    action: () => Promise<OrgEmbeddingRollout>,
    successMessage: string,
  ) => {
    setBusy(true)
    setError('')
    setSuccess('')
    try {
      const value = await action()
      setRollout(value)
      setSuccess(successMessage)
      onChanged?.()
    } catch (actionError) {
      const parsed = embeddingRolloutError(
        actionError,
        'Embedding rollout action failed.',
      )
      setError(
        parsed.status === 409 ? `Conflict: ${parsed.message}` : parsed.message,
      )
    } finally {
      setBusy(false)
    }
  }

  const docsValid = UUID_PATTERN.test(docsRunId.trim())
  const memoriesValid = UUID_PATTERN.test(memoriesRunId.trim())
  const enabled = rollout?.local_embeddings_enabled ?? localEmbeddingsEnabled
  const canVerify =
    rollout != null &&
    ['backfilling', 'verifying', 'failed', 'ready'].includes(rollout.state)

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography>Loading local embedding rollout…</Typography>
        </Stack>
      </Paper>
    )
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Local embedding rollout</Typography>
          <Typography variant="body2" color="text.secondary">
            Verification-gated rollout for this organisation only.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        {rollout?.last_error && (
          <Alert severity="error">
            Last rollout error: {rollout.last_error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              State
            </Typography>
            <Box>
              <Chip
                size="small"
                label={rollout?.state ?? 'not started'}
                color={stateColour(rollout?.state)}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Target profile
            </Typography>
            <Typography>{rollout?.target_profile ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Local embeddings enabled
            </Typography>
            <Typography>{enabled ? 'Yes' : 'No'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Documents
            </Typography>
            <Typography>
              {count(rollout?.docs_source_count ?? null)} source /{' '}
              {count(rollout?.docs_target_count ?? null)} local
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Memories
            </Typography>
            <Typography>
              {count(rollout?.memories_source_count ?? null)} source /{' '}
              {count(rollout?.memories_target_count ?? null)} local
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Last verified
            </Typography>
            <Typography>{timestamp(rollout?.verified_at ?? null)}</Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
            gap: 1,
            alignItems: 'start',
          }}
        >
          <TextField
            label="Documents migration run UUID"
            value={docsRunId}
            onChange={(event) => setDocsRunId(event.target.value)}
            error={docsRunId.length > 0 && !docsValid}
            helperText={
              docsRunId.length > 0 && !docsValid ? 'Enter a valid UUID.' : ' '
            }
            size="small"
          />
          <TextField
            label="Memories migration run UUID"
            value={memoriesRunId}
            onChange={(event) => setMemoriesRunId(event.target.value)}
            error={memoriesRunId.length > 0 && !memoriesValid}
            helperText={
              memoriesRunId.length > 0 && !memoriesValid
                ? 'Enter a valid UUID.'
                : ' '
            }
            size="small"
          />
          <Button
            variant="outlined"
            disabled={busy || !docsValid || !memoriesValid || enabled}
            onClick={() => {
              const restarting = rollout != null
              if (
                restarting &&
                !window.confirm(
                  'Restart this rollout with the supplied migration runs? Existing verification will be cleared.',
                )
              ) {
                return
              }
              void run(
                () =>
                  startOrgEmbeddingRollout(orgUuid, {
                    docs_run_id: docsRunId.trim(),
                    memories_run_id: memoriesRunId.trim(),
                  }),
                'Embedding rollout started.',
              )
            }}
          >
            Start rollout
          </Button>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            disabled={busy || !canVerify}
            onClick={() =>
              void run(
                () => verifyOrgEmbeddingRollout(orgUuid),
                'Embedding rollout verified.',
              )
            }
          >
            Verify
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={busy || rollout?.state !== 'ready'}
            onClick={() =>
              void run(
                () => activateOrgEmbeddingRollout(orgUuid),
                'Local embeddings activated.',
              )
            }
          >
            Activate
          </Button>
          <Button
            variant="outlined"
            color="warning"
            disabled={busy || rollout?.state !== 'enabled'}
            onClick={() => {
              if (
                !window.confirm(
                  'Disable local embeddings and return this organisation to OpenAI?',
                )
              ) {
                return
              }
              void run(
                () => disableOrgEmbeddingRollout(orgUuid),
                'Local embeddings disabled.',
              )
            }}
          >
            Disable
          </Button>
        </Stack>

        <Alert severity={rollout?.post_enable_write_at ? 'warning' : 'info'}>
          Disable is safe only before the first local-only write. After local
          writes, the API returns 409 and OpenAI must be reconciled before
          rollback.
          {rollout?.post_enable_write_at && (
            <>
              {' '}
              A local-only write was recorded at{' '}
              {timestamp(rollout.post_enable_write_at)}.
            </>
          )}
        </Alert>
      </Stack>
    </Paper>
  )
}
